import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { Options } from 'react-select';
import { isEqual, isNull, isUndefined } from 'lodash/fp';

import { useAppDispatch } from '../../../../hooks/react-redux';
import { Dropdown, DropdownOption } from '../../../shared/dropdown/Dropdown';
import { PortfolioAnalyticsSurveyIndex, PortfolioAnalyticsSurveyQuestion, PortfolioDocumentField, PortfolioQuestion, setIsChangingQuestion, setPortfolioField, updatePortfolioQuestion, updatePortfolioQuestionFilter } from '../store';
import styles from '../PortfolioAnalytics.module.scss';
import { Text } from '../../../shared/text/Text';
import { PortfolioAnalyticsFilter } from './PortfolioAnalyticsFilters';
import { DocumentNameDB } from '../../documents/store';

interface PortfolioAnalyticsSurveyQuestionsProps {
    surveyQuestionIndex: number;
    portfolioAnalyticsSurvey: PortfolioAnalyticsSurveyQuestion[];
    setQuestionIndex: (index: number) => void;
    changingQuestion: boolean;
    filteredFields: PortfolioDocumentField[];
    askPortfolioQuestion: () => void;
    documentNames: DocumentNameDB[];
}

export const PortfolioAnalyticsSurveyQuestions: React.FC<PortfolioAnalyticsSurveyQuestionsProps> = ({
    surveyQuestionIndex,
    portfolioAnalyticsSurvey,
    setQuestionIndex,
    changingQuestion,
    filteredFields,
    askPortfolioQuestion,
    documentNames
}) => {

    const dispatch = useAppDispatch();

    const transitionToNextQuestion = useCallback(() => {
        setTimeout(() => {
            setQuestionIndex(surveyQuestionIndex + 1);
            dispatch(setIsChangingQuestion(false));
        }, 750);
    }, [dispatch, setQuestionIndex, surveyQuestionIndex]);

    const { question, selectedOption, options, key, userQuestion } = portfolioAnalyticsSurvey[surveyQuestionIndex];

    const resetNextSurveyQuestionsCheck = useCallback((newOption: string | null) => {
        const savedOption = (selectedOption as DropdownOption | null | undefined)?.value;
        const optionsNotEqual = !isEqual(newOption, savedOption);
        if (surveyQuestionIndex === PortfolioAnalyticsSurveyIndex.AGREEMENT_TYPE) {
            if (optionsNotEqual) {
                dispatch(updatePortfolioQuestionFilter('documentNameIds', []));
                dispatch(setPortfolioField(null));
                dispatch(updatePortfolioQuestion('question', ''));
            }
        }
        if (surveyQuestionIndex === PortfolioAnalyticsSurveyIndex.DATASET_FIELD) {
            if (optionsNotEqual) {
                dispatch(updatePortfolioQuestion('question', ''));
            }
        }
    }, [dispatch, selectedOption, surveyQuestionIndex]);

    const updateDocumentNameIdsFilter = useCallback((parentDatasetId: number) => {
        const allDocumentNameIds = documentNames.filter(({ datasetId }) => datasetId === parentDatasetId).map(({ documentNameId }) => documentNameId!);
        dispatch(updatePortfolioQuestionFilter('documentNameIds', allDocumentNameIds));
    }, [dispatch, documentNames]);

    const updateDropdownAnswer = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null, key: keyof PortfolioQuestion) => {
        const value = !isNull(dropdownValue) ? (dropdownValue as DropdownOption).value : null;
        resetNextSurveyQuestionsCheck(value);
        if (!isNull(value)) {
            let formattedValue: string | number = value;
            if (key === 'parentDatasetId') {
                formattedValue = parseInt(value);
                updateDocumentNameIdsFilter(formattedValue);
            }
            if (key === 'fieldId') {
                const field = filteredFields.find(({ id }) => id === value);
                if (!isUndefined(field)) {
                    dispatch(setPortfolioField(field));
                    // TODO: get the field settings if its a calculator field as we need it for showOutputField/showGracePeriod, will need to return this in getAvailableFields
                }
            } else {
                dispatch(updatePortfolioQuestion(key, formattedValue));
            }
            transitionToNextQuestion();
        }
    }, [dispatch, transitionToNextQuestion, filteredFields, resetNextSurveyQuestionsCheck, updateDocumentNameIdsFilter]);

    const updateQuestion = useCallback((key: keyof PortfolioQuestion, value: string) => dispatch(updatePortfolioQuestion(key, value)), [dispatch]);

    const keyPressEnter = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => e.key.toLowerCase() === 'enter' && askPortfolioQuestion(), [askPortfolioQuestion]);

    const surveyInput = useMemo(() => {
        if (isUndefined(options)) {
            return (
                <Text
                    testId=''
                    onChange={e => updateQuestion('question', e.target.value)}
                    value={userQuestion}
                    maxLength={500}
                    placeholder='Ask anything you like about the selected field...'
                    onKeyDown={keyPressEnter}
                    marginBottom='0px'
                />
            );
        }
        return (
            <Dropdown
                value={selectedOption as DropdownOption}
                options={options}
                onChange={value => updateDropdownAnswer(value, key)}
                isClearable={false}
            />
        );
    }, [key, options, selectedOption, updateDropdownAnswer, updateQuestion, userQuestion, keyPressEnter]);

    return (
        <div className={styles.surveyQuestionsWrapper}>
            <div className={styles.questionAndInputWrapper}>
                <div className={classnames(styles.surveyQuestion, { [styles.surveyQuestionFadeIn]: !changingQuestion && surveyQuestionIndex !== 0, [styles.surveyQuestionFadeOut]: changingQuestion })}>{question}</div>

                <div className={styles.surveyInput}>
                    <div className={styles.surveyDropdownWrapper}>
                        {surveyInput}
                    </div>
                </div>
            </div>
            {surveyQuestionIndex === 2 &&
                <PortfolioAnalyticsFilter />
            }
        </div>
    );
};
